import { useEffect, useState } from "react";

import { useAppContext } from "../contexts/AppContext";
import { usePanel } from "../contexts/PanelContext";
import { useModerator } from "../Moderator/Moderator";
import { useIsMobile } from "./useIsMobile";

const REGULAR_PALETTE_COLOR_SIZE = 36;
const REGULAR_PALETTE_GAP = 10;
const REGULAR_PALETTE_ACTION_COUNT = 2;
const REGULAR_PALETTE_INLINE_PADDING = 10;
const REGULAR_PALETTE_OVERFLOW_BUFFER = 8;

const getRegularPaletteMinWidth = (colorCount: number) =>
  colorCount * REGULAR_PALETTE_COLOR_SIZE +
  Math.max(colorCount - 1, 0) * REGULAR_PALETTE_GAP +
  REGULAR_PALETTE_ACTION_COUNT * REGULAR_PALETTE_COLOR_SIZE +
  REGULAR_PALETTE_ACTION_COUNT * REGULAR_PALETTE_GAP +
  REGULAR_PALETTE_INLINE_PADDING +
  REGULAR_PALETTE_OVERFLOW_BUFFER;

const getIsCompactPaletteDefault = (colorCount: number) =>
  typeof window !== "undefined" &&
  colorCount > 0 &&
  window.innerWidth < getRegularPaletteMinWidth(colorCount);

export const PALETTE_LAYOUT_SPRING = {
  type: "spring",
  stiffness: 360,
  damping: 34,
} as const;

export const useIsCompactPaletteDefault = () => {
  const { config } = useAppContext();
  const colorCount = config?.pallete.colors.length ?? 0;
  const [isCompactPaletteDefault, setIsCompactPaletteDefault] = useState(() =>
    getIsCompactPaletteDefault(colorCount),
  );

  useEffect(() => {
    const handleResize = () => {
      setIsCompactPaletteDefault(getIsCompactPaletteDefault(colorCount));
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [colorCount]);

  return isCompactPaletteDefault;
};

export const useCompactPalette = () => {
  const { settings } = useAppContext();
  const Panel = usePanel();
  const { state: moderatorState } = useModerator();
  const isMobile = useIsMobile();
  const isCompactPaletteDefault = useIsCompactPaletteDefault();
  const compactPaletteSetting = settings.get["palette.compact"];
  const sidebarOpen = Boolean(
    Panel.state.info ||
      Panel.state.settings ||
      Panel.state.whois ||
      moderatorState.showMenu ||
      moderatorState.inspectUser,
  );
  const compactPaletteForSidebar = Boolean(
    !isMobile && sidebarOpen && settings.get["ui.compactPaletteForSidebar"],
  );

  return Boolean(
    compactPaletteSetting ??
      (isCompactPaletteDefault || compactPaletteForSidebar),
  );
};
